/**
 * 📄 MODAL DÉTAIL FACTURE LOGISTICS
 * ==================================
 * Modal pour afficher tous les détails d'une facture client logistics
 *
 * Fonctionnalités:
 * - Affichage complet : En-tête, Client, Items, Totaux
 * - Référence dossier d'expédition
 * - Historique paiements (si applicable)
 * - Actions : Télécharger PDF, Enregistrer paiement
 * - Design professionnel avec Cards et Descriptions
 *
 * Pattern source:
 * - Inspiré de: src/gestion-souscription/components/InvoiceDetailModal.tsx
 * - Adapté pour: Context logistics (shipment, items avec unité)
 *
 * @see src/gestion-logistics/hooks/useInvoices.ts
 * @see src/gestion-logistics/types/invoice.types.ts
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Divider,
  message,
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Alert,
  Empty,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  DownloadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TruckOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import useInvoices from '../hooks/useInvoices';
import useCustomerPayments from '../hooks/useCustomerPayments';
import { Invoice, InvoiceItem } from '../types/invoice.types';
import { CustomerPayment } from '../types/payment.types';
import PaymentRecordModal from './PaymentRecordModal';
import { useLogisticsPermissions } from '../hooks/useLogisticsPermissions';

const { Title, Text } = Typography;

dayjs.locale('fr');

interface InvoiceDetailModalProps {
  visible: boolean;
  invoiceId: number | null;
  onClose: () => void;
  onRefresh?: () => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  visible,
  invoiceId,
  onClose,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState<Invoice | null>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const { fetchInvoiceById, downloadInvoicePDF } = useInvoices();
  const {
    customerPayments,
    fetchPaymentsByInvoice,
    deletePayment,
    loading: paymentsLoading,
  } = useCustomerPayments();
  const permissions = useLogisticsPermissions();
  const isProforma = invoiceDetail?.invoice_type === 'proforma';

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (
    amount: number | null | undefined,
    symbol?: string
  ): string => {
    const formatted = formatAmount(amount);
    const currencySymbol = symbol || invoiceDetail?.currency_symbol || 'XOF';
    return `${formatted} ${currencySymbol}`;
  };

  // Charger les détails de la facture
  useEffect(() => {
    if (visible && invoiceId) {
      fetchInvoiceDetail();
    }
  }, [visible, invoiceId]);

  const fetchInvoiceDetail = async () => {
    if (!invoiceId) return;

    setLoading(true);
    try {
      const invoice = await fetchInvoiceById(invoiceId);
      if (invoice) {
        setInvoiceDetail(invoice);
        await fetchPaymentsByInvoice(invoice.id);
      }
    } catch (error) {
      console.error('Erreur chargement facture:', error);
      message.error('Erreur lors du chargement de la facture');
    } finally {
      setLoading(false);
    }
  };

  // Télécharger le PDF
  const handleDownloadPDF = async () => {
    if (!invoiceDetail) {
      message.error('Aucune facture sélectionnée');
      return;
    }

    try {
      message.loading({ content: 'Génération du PDF...', key: 'pdfGen', duration: 0 });
      await downloadInvoicePDF(invoiceDetail.id, invoiceDetail.invoice_number);
      message.success({ content: 'PDF téléchargé avec succès', key: 'pdfGen' });
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      message.error({ content: 'Erreur lors du téléchargement', key: 'pdfGen' });
    }
  };

  // Enregistrer un paiement
  const handleRecordPayment = () => {
    if (!invoiceDetail) return;
    if (invoiceDetail.invoice_type === 'proforma') {
      message.info('Une proforma ne peut pas recevoir de paiement');
      return;
    }
    setPaymentModalVisible(true);
  };

  const handleDeletePayment = async (paymentId: number) => {
    const success = await deletePayment(paymentId);
    if (success) {
      await fetchInvoiceDetail();
      if (onRefresh) onRefresh();
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentModalVisible(false);
    await fetchInvoiceDetail();
    if (onRefresh) onRefresh();
  };

  // Rendu du statut
  const renderStatus = (status: string) => {
    if (isProforma) {
      return (
        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
          PROFORMA
        </Tag>
      );
    }

    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      paid: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        label: 'Payée',
      },
      partial: {
        color: 'warning',
        icon: <ClockCircleOutlined />,
        label: 'Partiellement payée',
      },
      unpaid: {
        color: 'default',
        icon: <ClockCircleOutlined />,
        label: 'Non payée',
      },
      overdue: {
        color: 'error',
        icon: <ExclamationCircleOutlined />,
        label: 'En retard',
      },
    };

    const config = statusConfig[status] || statusConfig.unpaid;

    return (
      <Tag color={config.color} icon={config.icon} style={{ fontSize: '14px', padding: '4px 12px' }}>
        {config.label}
      </Tag>
    );
  };

  // Colonnes de la table des items
  const itemsColumns: ColumnsType<InvoiceItem> = [
    {
      title: '#',
      dataIndex: 'line_order',
      key: 'line_order',
      width: 50,
      align: 'center',
      render: (order: number) => order || '-',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || 'N/A',
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center',
      render: (qty: number, record) => `${qty || 1} ${record.unit || ''}`,
    },
    {
      title: 'Prix Unitaire',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 130,
      align: 'right',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount: number) => (
        <Text strong style={{ fontSize: '14px' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Détails de la Facture</span>
          {invoiceDetail && renderStatus(invoiceDetail.payment_status)}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadPDF}
        >
          Télécharger PDF
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : invoiceDetail ? (
        <div>
          {/* En-tête avec numéro de facture et montant total */}
          <Card style={{ marginBottom: 16, backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col span={12}>
                <Space direction="vertical" size={0}>
                  <Text type="secondary">Facture N°</Text>
                  <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                    {invoiceDetail.invoice_number}
                  </Title>
                  {invoiceDetail.shipment_number && (
                    <Text type="secondary">
                      <TruckOutlined /> Dossier: {invoiceDetail.shipment_number}
                    </Text>
                  )}
                </Space>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Space direction="vertical" size={0} style={{ width: '100%', alignItems: 'flex-end' }}>
                  <Text type="secondary">Montant Total</Text>
                  <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                    {formatCurrency(invoiceDetail.total_amount)}
                  </Title>
                </Space>
              </Col>
            </Row>
          </Card>

          {isProforma && (
            <Alert
              message="Proforma"
              description="Cette proforma n'est pas une facture definitive et ne constitue pas une demande de paiement."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Alerte si facture payée */}
          {!isProforma && invoiceDetail.payment_status === 'paid' && invoiceDetail.paid_amount > 0 && (
            <Alert
              message="Facture Entièrement Payée"
              description={
                <div>
                  <p><strong>Montant payé:</strong> {formatCurrency(invoiceDetail.paid_amount)}</p>
                  {invoiceDetail.updated_at && (
                    <p><strong>Date de paiement:</strong> {dayjs(invoiceDetail.updated_at).format('DD MMMM YYYY')}</p>
                  )}
                </div>
              }
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Informations client et dates */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card title="Informations Client" size="small">
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Nom">
                    <Text strong>{invoiceDetail.client_name || 'N/A'}</Text>
                  </Descriptions.Item>
                  {invoiceDetail.client_address && (
                    <Descriptions.Item label="Adresse">
                      {invoiceDetail.client_address}
                    </Descriptions.Item>
                  )}
                  {invoiceDetail.client_email && (
                    <Descriptions.Item label="Email">
                      {invoiceDetail.client_email}
                    </Descriptions.Item>
                  )}
                  {invoiceDetail.client_phone && (
                    <Descriptions.Item label="Téléphone">
                      {invoiceDetail.client_phone}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="Dates" size="small">
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Date d'émission">
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    {dayjs(invoiceDetail.invoice_date || invoiceDetail.created_at).format('DD MMMM YYYY')}
                  </Descriptions.Item>
                  {invoiceDetail.due_date && (
                    <Descriptions.Item label="Date d'échéance">
                      <CalendarOutlined style={{ marginRight: 8 }} />
                      <Text
                        strong
                        style={{
                          color:
                            invoiceDetail.payment_status === 'overdue'
                              ? '#ff4d4f'
                              : 'inherit',
                        }}
                      >
                        {dayjs(invoiceDetail.due_date).format('DD MMMM YYYY')}
                      </Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Créée le">
                    {dayjs(invoiceDetail.created_at).format('DD/MM/YYYY à HH:mm')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>

          <Divider>Lignes de la Facture</Divider>

          {/* Table des items */}
          <Table
            columns={itemsColumns}
            dataSource={invoiceDetail.items || []}
            rowKey="id"
            size="small"
            pagination={false}
            style={{ marginBottom: 16 }}
            locale={{
              emptyText: 'Aucune ligne dans cette facture',
            }}
          />

          {/* Totaux */}
          <Row justify="end">
            <Col span={10}>
              <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Sous-total HT:</Text>
                    <Text strong style={{ fontSize: '15px' }}>
                      {formatCurrency(invoiceDetail.subtotal)}
                    </Text>
                  </div>

                  {invoiceDetail.tax_amount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>
                        Taxes ({invoiceDetail.tax_rate || 0}%):
                      </Text>
                      <Text strong style={{ fontSize: '15px' }}>
                        {formatCurrency(invoiceDetail.tax_amount)}
                      </Text>
                    </div>
                  )}

                  <Divider style={{ margin: '8px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: '16px' }}>
                      TOTAL TTC:
                    </Text>
                    <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                      {formatCurrency(invoiceDetail.total_amount)}
                    </Text>
                  </div>

                  {invoiceDetail.paid_amount > 0 && (
                    <>
                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="success">Montant payé:</Text>
                        <Text strong style={{ fontSize: '15px', color: '#52c41a' }}>
                          {formatCurrency(invoiceDetail.paid_amount)}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="warning">Montant dû:</Text>
                        <Text strong style={{ fontSize: '15px', color: '#faad14' }}>
                          {formatCurrency(invoiceDetail.balance_due)}
                        </Text>
                      </div>
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {!isProforma && (
            <>
              <Divider>Paiements</Divider>

              <Card
                size="small"
                title="Historique Paiements"
                extra={
                  invoiceDetail.balance_due > 0 &&
                  permissions.canCreateCustomerPayments && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleRecordPayment}
                    >
                      Nouveau Paiement
                    </Button>
                  )
                }
              >
                {customerPayments.length > 0 ? (
                  <Table
                    dataSource={customerPayments}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    loading={paymentsLoading}
                    columns={[
                      {
                        title: 'Date',
                        dataIndex: 'payment_date',
                        key: 'payment_date',
                        width: 100,
                        render: (date: string) =>
                          dayjs(date).format('DD/MM/YYYY'),
                      },
                      {
                        title: 'Montant',
                        dataIndex: 'amount',
                        key: 'amount',
                        align: 'right',
                        width: 120,
                        render: (amount: number, record: CustomerPayment) => (
                          <Text strong style={{ color: '#52c41a' }}>
                            {formatCurrency(amount, record.currency_symbol)}
                          </Text>
                        ),
                      },
                      {
                        title: 'Mode',
                        dataIndex: 'payment_mode_name',
                        key: 'payment_mode_name',
                        width: 140,
                        render: (_: string, record: CustomerPayment) =>
                          record.payment_mode_name ||
                          record.payment_mode_name_full ||
                          '-',
                      },
                      {
                        title: 'Reference',
                        dataIndex: 'payment_reference',
                        key: 'payment_reference',
                        width: 140,
                        render: (reference?: string) => reference || '-',
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        width: 60,
                        align: 'center',
                        render: (_, record: CustomerPayment) =>
                          permissions.canDeleteCustomerPayments && (
                            <Popconfirm
                              title="Supprimer ce paiement ?"
                              description="Le solde de la facture sera recalcule automatiquement."
                              onConfirm={() => handleDeletePayment(record.id)}
                              okText="Supprimer"
                              cancelText="Annuler"
                              okButtonProps={{ danger: true }}
                            >
                              <Tooltip title="Supprimer">
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                />
                              </Tooltip>
                            </Popconfirm>
                          ),
                      },
                    ]}
                  />
                ) : (
                  <Empty
                    description="Aucun paiement enregistre"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    {invoiceDetail.balance_due > 0 &&
                      permissions.canCreateCustomerPayments && (
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={handleRecordPayment}
                        >
                          Enregistrer le 1er Paiement
                        </Button>
                      )}
                  </Empty>
                )}
              </Card>
            </>
          )}

          {/* Notes */}
          {invoiceDetail.notes && (
            <div style={{ marginTop: 16 }}>
              <Divider>Notes</Divider>
              <Card size="small" style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                <Text>{invoiceDetail.notes}</Text>
              </Card>
            </div>
          )}

          {/* Conditions générales */}
          {invoiceDetail.terms_and_conditions && (
            <div style={{ marginTop: 16 }}>
              <Divider>Conditions Générales</Divider>
              <Card size="small">
                <Text style={{ fontSize: '12px' }}>{invoiceDetail.terms_and_conditions}</Text>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">Aucune facture sélectionnée</Text>
        </div>
      )}
      {!isProforma && (
        <PaymentRecordModal
          visible={paymentModalVisible}
          invoice={invoiceDetail}
          onClose={() => setPaymentModalVisible(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Modal>
  );
};

export default InvoiceDetailModal;
